import { Button, Form, Input } from '@bancobarigui/barihaus';
import { Table, Space, Modal, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { ProtectedRouteContainer } from '@/components/ProtectedRouteContainer';
import { PageHeader } from '@/components/PageHeader';
import { ServiceCheck, FeatureAlert } from '@/shared/components';
import { StatusBadge } from '@/components/StatusBadge';
import { useInspectionReasonList } from './useInspectionReasonList';
import type { InspectionReason } from '@/services/types/user.types';

export function InspectionReasonList() {
  const {
    inspectionReasons,
    loading,
    isModalOpen,
    editingItem,
    submitting,
    form,
    handleNew,
    handleEdit,
    handleDelete,
    handleModalCancel,
    handleModalSubmit,
  } = useInspectionReasonList();

  const tableColumns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: InspectionReason, b: InspectionReason) => a.name.localeCompare(b.name),
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description',
      render: (description: string | null) => description || '-',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <StatusBadge
          label={isActive ? 'Ativo' : 'Inativo'}
          color={isActive ? '#22c55e' : '#ef4444'}
        />
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 200,
      render: (_: unknown, record: InspectionReason) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Excluir
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <ProtectedRouteContainer
      pageHeader={
        <PageHeader
          title="Motivos de Inspeção"
          actions={
            <Button buttonColor="primary" icon={<PlusOutlined />} onClick={handleNew}>
              Novo Motivo de Inspeção
            </Button>
          }
        />
      }
    >
      <FeatureAlert services={['user']} />
      <ServiceCheck services={['user']} featureName="Motivos de Inspecao">
        <div className="flex flex-col gap-4">
        <Table
          columns={tableColumns}
          dataSource={inspectionReasons}
          loading={loading}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total de ${total} motivos`,
          }}
          scroll={{ x: 1000 }}
        />

        <Modal
          title={editingItem ? 'Editar Motivo de Inspeção' : 'Novo Motivo de Inspeção'}
          open={isModalOpen}
          onCancel={handleModalCancel}
          onOk={handleModalSubmit}
          confirmLoading={submitting}
          okText={editingItem ? 'Salvar' : 'Criar'}
          cancelText="Cancelar"
        >
          <Form
            form={form}
            layout="vertical"
            className="mt-4"
          >
            <Form.Item
              name="name"
              label="Nome"
              rules={[{ required: true, message: 'Por favor, informe o nome' }]}
            >
              <Input placeholder="Nome do motivo de inspeção" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Descrição"
            >
              <Input placeholder="Descrição (opcional)" />
            </Form.Item>

            {editingItem && (
              <Form.Item
                name="isActive"
                label="Ativo"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch />
              </Form.Item>
            )}
          </Form>
        </Modal>
        </div>
      </ServiceCheck>
    </ProtectedRouteContainer>
  );
}
